import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Patient.module.scss";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  getPatientNameWithStatus,
  patientIdentifierQueryParamKey,
  patientsWithUnhandledMeasurementsQueryOptions,
  usePatient,
} from "@/api/Patients";
import { Tab, TabList } from "@medoma/components/Tabs";
import { deducedError } from "@/Utils/ErrorUtils";
import { z } from "zod";
import { Heading } from "@medoma/components/Heading/Heading";
import { CommunicationTab } from "./CommunicationTab";
import { getLogisticsApiUrl, getNotificationApiUrl } from "@/Utils/EnvUtils";
import { useQuery } from "@tanstack/react-query";
import {
  formatPersonalIdentityNumber,
  getPatientFullName,
  patientStatusSchema,
  type IDischargedPatient,
  type IDeclinedPatient,
  type IRegisteredPatient,
} from "@medoma/models/patients";
import {
  FilledButton,
  OutlinedButton,
  PlainButton,
} from "@medoma/components/Button/Button";
import { Text } from "@medoma/components/Text/Text";
import * as Sentry from "@sentry/react";
import { NotificationCircle } from "@medoma/components/NotificationCircle/NotificationCircle";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { useUserProfile } from "@/api/Users";
import { patientSessionQueryOptions } from "@/api/Auth";
import LoggedIntoAppChip from "@/components/Chips/LoggedIntoAppChip";
import NotUsingAppChip from "@/components/Chips/NotUsingAppChip";
import { generateQueryString } from "@/api/Helpers";
import { DeclineDialog } from "./Decline/DeclineDialog";
import { logisticsConfigQueryOptions } from "@/api/Config";
import { BackButton } from "@/components/NavigationButtons/NavigationButtons";

type PatientEvent = MessageEvent<{ type: string; patientId: string }>;

const ADMIT_PATH = "/admit";

const AdmitOrReject = ({ patient }: { patient: IRegisteredPatient }) => {
  const navigate = useNavigate();
  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);

  return (
    <div className={styles.buttonGroup}>
      <OutlinedButton onClick={() => setIsDeclineDialogOpen(true)}>
        <Trans>Avslå</Trans>
      </OutlinedButton>
      <FilledButton onClick={() => navigate("admit")}>
        <Trans>Fullfölj inskrivning</Trans>
      </FilledButton>
      {isDeclineDialogOpen ? (
        <DeclineDialog
          isOpen={isDeclineDialogOpen}
          onClose={() => setIsDeclineDialogOpen(false)}
          onDeclined={() => {
            setIsDeclineDialogOpen(false);
            void navigate("..");
          }}
          patient={patient}
        />
      ) : null}
    </div>
  );
};

const ReadmitButton = ({
  patient,
}: {
  patient: IDischargedPatient | IDeclinedPatient;
}) => {
  const navigate = useNavigate();

  const handleReadmit = () => {
    const { personalIdentityNumber, personalIdentityNumberType } = patient;
    const queryString = generateQueryString({
      [patientIdentifierQueryParamKey[personalIdentityNumberType]]:
        personalIdentityNumber,
    });
    void navigate(`/commandcenter/patients/register/returning${queryString}`);
  };

  return (
    <PlainButton onClick={handleReadmit}>
      <Trans>Återregistrera patient</Trans>
    </PlainButton>
  );
};

const Patient = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { organization } = useUserProfile();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { data: patient, isError, isPending, error } = usePatient(patientId);
  const { data: patientsWithUnhandledMeasurements } = useQuery(
    patientsWithUnhandledMeasurementsQueryOptions,
  );
  const location = useLocation();
  const shouldShowProspectButtons = !location.pathname.endsWith(ADMIT_PATH);

  const { data: isPatientSignedIn, isPending: isPendingPatientSignedIn } =
    useQuery(patientSessionQueryOptions(patientId));
  const hasUnhandledMeasurements = patientsWithUnhandledMeasurements?.find(
    ({ id, unhandledMeasurementsCount }) =>
      id === patientId && unhandledMeasurementsCount > 0,
  )
    ? true
    : false;
  const { data: patientNoteIsEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CarePatientNote,
  );
  const { data: equipmentManagementEnabled } = useQuery({
    ...logisticsConfigQueryOptions(),
    select: (data) => data.equipmentManagement.enabled,
  });
  useEffect(() => {
    const handleShowNotification = (event: PatientEvent) => {
      if (event.origin !== getLogisticsApiUrl(organization)) {
        return;
      }
      if (event.data.type !== "patientTabletAckMissing") {
        return;
      }
      if (event.data.patientId !== patient?.id) {
        return;
      }
      const patientName = getPatientFullName(patient.name);
      // oxlint-disable-next-line compat/compat -- center runs in desktop browsers, which all have Notification
      new Notification(t`Misslyckades att notifiera patient ${patientName}`, {
        body: t`Det ringer inte hos ${patientName}. Pröva att ringa igen, eller kontakta patienten på annat sätt.`,
        requireInteraction: true,
      });
    };

    window.addEventListener("message", handleShowNotification);

    return () => {
      window.removeEventListener("message", handleShowNotification);
    };
  }, [patient?.name, patient?.id, organization, t]);

  if (isPending) {
    return <Loading message={t`Laddar patientdetaljer`} padding={24} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta patientdetaljer.`} ${deducedError(error)}`}
        padding={24}
      />
    );
  }

  return (
    <div className={styles.patient}>
      {/* This iframe is used to setup an SSE subscription to notifications from the Backend.
          We can then act on these events, for example, showing an error message when the patient-tablet 
          has not received a notification for 'video-call-started'. */}
      <iframe
        style={{ display: "none" }}
        title="Patient notifications"
        src={`${getNotificationApiUrl(organization)}/htmx/patient/${patientId}/sse`}
      />

      <header className={styles.header}>
        <div className={styles.topRow}>
          <BackButton
            navigateBack={() => navigate("..")}
            ariaLabel={t`Tillbaka till patienter`}
          />
          <Heading level="h1">
            {getPatientNameWithStatus(patient, "full")}
          </Heading>
          <Text element="div" size="large">
            {formatPersonalIdentityNumber({
              personalIdentityNumber: patient.personalIdentityNumber,
              personalIdentityNumberType: patient.personalIdentityNumberType,
            })}
          </Text>
          {patient.status === patientStatusSchema.enum.discharged ||
          patient.status === patientStatusSchema.enum.declined ? (
            <ReadmitButton patient={patient} />
          ) : isPendingPatientSignedIn ? null : isPatientSignedIn ? (
            <LoggedIntoAppChip />
          ) : (
            <NotUsingAppChip />
          )}
          {patient.status === patientStatusSchema.enum.prospect &&
          shouldShowProspectButtons ? (
            <AdmitOrReject patient={patient} />
          ) : (
            <></>
          )}
        </div>
        <TabList>
          <Tab to="activities">
            <Trans>Aktiviteter</Trans>
          </Tab>
          <Tab to="measurements">
            <NotificationCircle
              isVisible={hasUnhandledMeasurements}
              placement="right"
            >
              <Trans>Mätvärden</Trans>
            </NotificationCircle>
          </Tab>
          <CommunicationTab patientId={patient.id} />
          <Tab to="information">
            <Trans>Information</Trans>
          </Tab>
          {equipmentManagementEnabled ? (
            <Tab to="equipment">
              <Trans>Utrustning</Trans>
            </Tab>
          ) : (
            <></>
          )}
          <Tab to="authentication">
            <Trans>Inloggning</Trans>
          </Tab>
          {patientNoteIsEnabled ? (
            <Tab to="patient-note">
              <Trans>Patientanteckning</Trans>
            </Tab>
          ) : (
            <></>
          )}
          <Tab to="care-episode">
            <Trans>Vårdperiod</Trans>
          </Tab>
        </TabList>
      </header>

      <div className={styles.content}>
        <Outlet context={patient} />
      </div>
    </div>
  );
};

export default Patient;
